import React, { useState, useEffect, useCallback } from 'react';
import './CompanyInfoPage.css';
import { useAtomValue } from 'jotai';
import { supervisorsListState } from '../jotai/atoms';
import axiosInstance from '../api/axiosInstance';
import StaffModal from './StaffModal';
import CompanyInfoModal from './CompanyInfoModal';
import Swal from 'sweetalert2';
import { useAuth } from '../context/AuthContext';

function CompanyInfoPage() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const supervisors = useAtomValue(supervisorsListState);
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [currentRole, setCurrentRole] = useState('');

  const [companyDetails, setCompanyDetails] = useState({});
  const [isCompanyInfoModalOpen, setIsCompanyInfoModalOpen] = useState(false);

  const fetchStaff = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/staff');
      setStaff(response.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  }, []);

  const fetchCompanyInfo = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/company-info');
      setCompanyDetails(response.data);
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  }, []);

  useEffect(() => {
    fetchStaff();
    fetchCompanyInfo();
  }, [fetchStaff, fetchCompanyInfo]);

  const companyName = "(주) 전우종합기술";

  const ceo = staff.find(s => s.role === 'CEO');
  const executives = staff.filter(s => s.role === 'EXECUTIVE');
  const employees = staff.filter(s => s.role === 'EMPLOYEE');

  const handleAddStaff = (role) => {
    setCurrentRole(role);
    setEditingStaff(null);
    setIsStaffModalOpen(true);
  };

  const handleEditStaff = (staffMember) => {
    setEditingStaff(staffMember);
    setIsStaffModalOpen(true);
  };

  const handleDeleteStaff = async (id) => {
    Swal.fire({
      title: '정말로 삭제하시겠습니까?',
      text: "삭제하면 되돌릴 수 없습니다!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '예, 삭제합니다!',
      cancelButtonText: '취소',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axiosInstance.delete(`/staff/${id}`);
          fetchStaff();
          Swal.fire('삭제됨!', '직원 정보가 삭제되었습니다.', 'success');
        } catch (error) {
          console.error('Error deleting staff:', error);
          Swal.fire('오류!', '직원 정보 삭제 중 오류가 발생했습니다.', 'error');
        }
      }
    });
  };

  const handleSubmitStaff = async (formData) => {
    try {
      if (editingStaff) {
        await axiosInstance.put(`/staff/${editingStaff.id}`, formData);
      } else {
        await axiosInstance.post('/staff', formData);
      }
      fetchStaff();
      setIsStaffModalOpen(false);
      setEditingStaff(null);
    } catch (error) {
      console.error('Error submitting staff data:', error);
    }
  };

  const handleEditCompanyInfo = () => {
    setIsCompanyInfoModalOpen(true);
  };

  const handleSaveCompanyInfo = async (formData) => {
    try {
      await axiosInstance.post('/company-info', formData);
      fetchCompanyInfo();
      setIsCompanyInfoModalOpen(false);
      Swal.fire('저장 완료!', '회사 정보가 성공적으로 저장되었습니다.', 'success');
    } catch (error) {
      console.error('Error saving company info:', error);
      Swal.fire('오류!', '회사 정보 저장 중 오류가 발생했습니다.', 'error');
    }
  };

  const StaffCard = ({ staff, isSupervisor = false }) => {
    if (isSupervisor) {
      return (
        <div className="staff-card">
          <p><strong>이름:</strong> {staff.name}</p>
          <p><strong>전화번호:</strong> {staff.phone}</p>
          <p><strong>등급:</strong> {staff.position}</p>
          <p><strong>생년월일:</strong> {staff.birthDate}</p>
        </div>
      );
    }

    return (
      <div className="staff-card">
        <p><strong>이름:</strong> {staff.name}</p>
        <p><strong>전화번호:</strong> {staff.phone}</p>
        <p><strong>직책:</strong> {staff.position}</p>
        {staff.role !== 'CEO' && <p><strong>소속:</strong> {staff.department}</p>}
        {user && user.role === 'ADMIN' && (
          <div className="card-actions">
            <button onClick={() => handleEditStaff(staff)}>수정</button>
            <button onClick={() => handleDeleteStaff(staff.id)}>삭제</button>
          </div>
        )}
      </div>
    );
  };

  const RoleSection = ({ title, role, children }) => (
    <div className="role-section">
      <div className="role-section-header">
        <h2>{title}</h2>
        {user && user.role === 'ADMIN' && role && <button onClick={() => handleAddStaff(role)}>추가</button>}
      </div>
      {children}
    </div>
  );

  return (
    <div className="company-info-container left-aligned">
      <h1 className="company-title">{companyName}</h1>
      
      <RoleSection title="대표이사" role="CEO">
        {ceo ? <StaffCard staff={ceo} /> : user && user.role === 'ADMIN' ? <p>대표이사를 추가해주세요. <button onClick={() => handleAddStaff('CEO')}>추가</button></p> : <p>대표이사를 추가해주세요.</p>}
      </RoleSection>

      <RoleSection title="임원" role="EXECUTIVE">
        <div className="card-group">
          {executives.length > 0 ? executives.map((staff, index) => <StaffCard key={index} staff={staff} />) : user && user.role === 'ADMIN' ? <p>임원을 추가해주세요. <button onClick={() => handleAddStaff('EXECUTIVE')}>추가</button></p> : <p>임원을 추가해주세요.</p>}
        </div>
      </RoleSection>

      <RoleSection title="직원" role="EMPLOYEE">
        <div className="card-group">
          {employees.length > 0 ? employees.map((staff, index) => <StaffCard key={index} staff={staff} />) : user && user.role === 'ADMIN' ? <p>직원을 추가해주세요. <button onClick={() => handleAddStaff('EMPLOYEE')}>추가</button></p> : <p>직원을 추가해주세요.</p>}
        </div>
      </RoleSection>

      <RoleSection title="감리원">
        <div className="supervisor-grades">
          <h3>특급</h3>
          <div className="card-group">
            {supervisors.filter(s => s.position === '특급').map((staff, index) => <StaffCard key={index} staff={staff} isSupervisor={true} />)}
          </div>
          <h3>고급</h3>
          <div className="card-group">
            {supervisors.filter(s => s.position === '고급').map((staff, index) => <StaffCard key={index} staff={staff} isSupervisor={true} />)}
          </div>
          <h3>중급</h3>
          <div className="card-group">
            {supervisors.filter(s => s.position === '중급').map((staff, index) => <StaffCard key={index} staff={staff} isSupervisor={true} />)}
          </div>
          {supervisors.filter(s => !['특급', '고급', '중급'].includes(s.position)).length > 0 && (
            <>
              <h3>기타</h3>
              <div className="card-group">
                {supervisors.filter(s => !['특급', '고급', '중급'].includes(s.position)).map((staff, index) => <StaffCard key={index} staff={staff} isSupervisor={true} />)}
              </div>
            </>
          )}
        </div>
      </RoleSection>

      <div className="company-info-footer">
        <h2>회사 정보</h2>
        {companyDetails.address && <p><strong>주소:</strong> {companyDetails.address}</p>}
        {companyDetails.email && <p><strong>이메일:</strong> {companyDetails.email}</p>}
        {companyDetails.businessRegistrationNumber && <p><strong>사업자 번호:</strong> {companyDetails.businessRegistrationNumber}</p>}
        {companyDetails.phoneNumber && <p><strong>전화번호:</strong> {companyDetails.phoneNumber}</p>}
        {user && user.role === 'ADMIN' && (
          <button onClick={handleEditCompanyInfo}>회사 정보 수정</button>
        )}
      </div>

      <StaffModal 
        isOpen={isStaffModalOpen} 
        onClose={() => setIsStaffModalOpen(false)} 
        onSubmit={handleSubmitStaff} 
        initialData={editingStaff} 
        role={currentRole} 
      />

      <CompanyInfoModal
        isOpen={isCompanyInfoModalOpen}
        onClose={() => setIsCompanyInfoModalOpen(false)}
        onSubmit={handleSaveCompanyInfo}
        initialData={companyDetails}
      />
    </div>
  );
}

export default CompanyInfoPage;